export default {
  slug: "/teeth-whitening",
  group: { href: "/dental", label: { en: "Dental care", ar: "طب الأسنان" } },
  heroImage: "/assets/images/ai/services/dental/hero.webp",

  en: {
    title: "Teeth Whitening in Istanbul",
    eyebrow: "Dental care",
    lead:
      "Whitening changes the colour of natural teeth with peroxide — in the chair at a controlled " +
      "concentration, or at home in a custom tray over two to four weeks. It does not change the colour of " +
      "crowns, veneers or fillings, and that limit shapes the whole plan.",
    cta: "Book a free assessment",
    heroAlt: "Teeth whitening assessment at Platinya Clinic",
    stats: [
      { value: "45–90 min", label: "In-office session" },
      { value: "1–2 sessions", label: "Usually enough" },
      { value: "6–24 months", label: "Before a touch-up" },
    ],
    candidate: {
      heading: "Who is a good candidate",
      intro:
        "Most healthy adult teeth respond to some degree. What varies is the type of stain, the starting " +
        "shade, and whether the colour sits in the enamel or deeper inside the tooth.",
      items: [
        "Yellow or greyish natural teeth, usually age-related or from tea, coffee and tobacco",
        "Staining that returns soon after a professional clean and polish",
        "Before veneers or crowns, so the ceramic can be matched to a lighter shade rather than the other way round",
        "Healthy gums and no untreated decay: whitening over a leaking filling or exposed dentine gives pain, not whiteness",
      ],
      note:
        "Crowns, veneers, composite fillings and bonding do not lighten. Whitening a mouth with visible " +
        "restorations can leave them looking darker by contrast; either they are replaced afterwards, or the " +
        "shade is matched to them instead.",
    },
    techniques: {
      heading: "In the chair, at home, or both",
      intro:
        "The two approaches use the same chemistry at different concentrations. Which one suits you depends " +
        "on how much time you have and how your teeth react.",
      items: [
        {
          name: "In-office whitening",
          text:
            "A high-concentration hydrogen peroxide gel — typically 25–40% — applied with the gums isolated " +
            "and the lips retracted, in three or four cycles of 10–15 minutes. One visit, fastest result; " +
            "sensitivity usually peaks the following day.",
        },
        {
          name: "Custom-tray whitening at home",
          text:
            "Impressions are taken and trays made to fit. Carbamide peroxide at 10–16% is worn for 30–60 " +
            "minutes a day, or a weaker gel overnight, for two to four weeks. Slower and gentler, and the " +
            "result is often more even because the gel reaches the whole arch.",
        },
        {
          name: "Combined",
          text:
            "A chair session to lift the base shade, then trays at home for a week or two to even out the " +
            "neck of the tooth, which is usually the darkest part. This is the plan most patients end up " +
            "with.",
        },
      ],
    },
    journey: {
      heading: "From consultation to the final shade",
      steps: [
        {
          name: "Assessment and clean",
          text:
            "Decay, gum health and existing restorations are checked. Scaling and polishing come first: " +
            "whitening does not remove calculus, and a clean surface is the only fair starting point.",
        },
        {
          name: "Shade record",
          text:
            "Photographs and a shade reading under standard light, taken before anything begins. Without " +
            "this, the change can only be argued about, not measured.",
        },
        {
          name: "Whitening",
          text:
            "Either a single chair session with gum isolation, or trays and written instructions. With " +
            "trays, the first two or three days show whether your teeth are sensitive and the gel strength " +
            "is adjusted accordingly.",
        },
        {
          name: "Review at two weeks",
          text:
            "The colour stabilises over about two weeks as the teeth rehydrate, so the final shade is judged " +
            "then — not on the day of treatment, when teeth always look unnaturally bright.",
        },
        {
          name: "Maintenance",
          text:
            "Top-up trays every six to twenty-four months, and a sensible first 48 hours: no coffee, red " +
            "wine, turmeric or smoking while the enamel is most receptive.",
        },
      ],
    },
    risks: {
      heading: "What can go wrong",
      intro:
        "Whitening is low-risk when it is assessed and supervised. Most of the problems come from doing it " +
        "without either.",
      items: [
        "Sensitivity during treatment and for a day or two after, sometimes longer; existing gum recession makes it worse",
        "Gum irritation or blanching where gel reaches soft tissue — usually transient and settles in days",
        "Little or no change on internal stains, a root-treated tooth, or fluorosis",
        "Restorations keep their original shade and can look darker once the teeth around them lighten",
        "Relapse: the result is not permanent, and how fast it fades follows diet and smoking",
      ],
      note:
        "More gel does not mean whiter. Repeated high-strength sessions without review increase sensitivity " +
        "and give the enamel a chalky, thin appearance; the tray instructions exist for a reason.",
    },
    faq: {
      heading: "Questions patients ask",
      items: [
        {
          q: "Does it damage the enamel?",
          a:
            "At supervised concentrations, no: peroxide lifts pigment without dissolving measurable tooth " +
            "structure, and the mineral recovers in the days afterwards. Abuse — frequent high-strength " +
            "sessions with no assessment — is where damage and lasting sensitivity come from.",
        },
        {
          q: "How white will my teeth get?",
          a:
            "It cannot be promised in advance. Yellow and age-related staining responds best; grey, " +
            "tetracycline-related and fluorosis staining responds poorly and sometimes not at all. Most " +
            "patients gain a few shades, and the honest answer is a range rather than a number.",
        },
        {
          q: "Is it painful?",
          a:
            "The gel itself is not painful. Sensitivity to cold, and occasionally a sharp ache at the neck of " +
            "the tooth, is common for a day or two — reduced by a lower concentration, a shorter wear time, " +
            "or a desensitising gel before the session.",
        },
        {
          q: "How long does it last?",
          a:
            "Six months to two years for most people. Coffee, tea, red wine, turmeric and smoking bring it " +
            "back faster; a hygiene visit every six months and the occasional top-up tray keep it steady.",
        },
      ],
    },
    ctaBand: {
      heading: "Send a photograph in daylight",
      text:
        "A photograph taken near a window, without a filter, is usually enough to say which kind of stain " +
        "you have, whether whitening is likely to work, and whether restorations need to be planned around it.",
      button: "Send your photos",
    },
    disclaimer:
      "General information, not medical advice. Suitability can only be judged after examining the teeth and " +
      "gums and reviewing the medical history; results vary between patients.",
  },

  ar: {
    title: "تبييض الأسنان في إسطنبول",
    eyebrow: "طب الأسنان",
    lead:
      "التبييض يغيّر لون الأسنان الطبيعية بالبيروكسيد — في العيادة بتركيز محكوم، أو في المنزل بقالب " +
      "مخصّص على مدى أسبوعين إلى أربعة أسابيع. لكنه لا يغيّر لون التركيبات والقشور والحشوات، وهذا الحدّ " +
      "هو ما يشكّل الخطة كلها.",
    cta: "احجز تقييماً مجانياً",
    heroAlt: "تقييم تبييض الأسنان في عيادة بلاتينيا",
    stats: [
      { value: "٤٥–٩٠ دقيقة", label: "الجلسة في العيادة" },
      { value: "١–٢ جلسة", label: "تكفي عادةً" },
      { value: "٦–٢٤ شهراً", label: "قبل جلسة إنعاش" },
    ],
    candidate: {
      heading: "من هو المرشّح المناسب",
      intro:
        "معظم الأسنان السليمة عند البالغين تستجيب بدرجة ما. والفرق يكمن في نوع التصبّغ، والدرجة التي " +
        "تبدأ منها، وهل اللون في المينا أم أعمق داخل السن.",
      items: [
        "أسنان صفراء أو رمادية طبيعية، غالباً بحسب العمر أو بسبب الشاي والقهوة والتبغ",
        "تصبّغ يعود سريعاً بعد التنظيف والتلميع في العيادة",
        "قبل القشور أو التركيبات، لتُطابَق القطعة الخزفية مع درجة أفتح لا العكس",
        "لثة سليمة ولا تسوّس غير معالَج: فالتبييض فوق حشوة متسرّبة أو عاج مكشوف يعطي ألماً لا بياضاً",
      ],
      note:
        "التركيبات والقشور والحشوات الراتنجية لا يتغيّر لونها. وتبييض فم فيه ترميمات ظاهرة قد يجعلها تبدو " +
        "أغمق بالمقارنة؛ فإمّا تُستبدل بعد التبييض، وإمّا يُطابق التبييض على لونها بدل ذلك.",
    },
    techniques: {
      heading: "في العيادة أم في المنزل",
      intro:
        "الطريقتان تستخدمان الكيمياء نفسها بتركيزين مختلفين. والاختيار بينهما يعتمد على الوقت المتاح وعلى " +
        "استجابة أسنانك.",
      items: [
        {
          name: "التبييض في العيادة",
          text:
            "جل بيروكسيد هيدروجين عالي التركيز — ٢٥ إلى ٤٠٪ عادةً — يُوضع مع عزل اللثة وسحب الشفتين، في " +
            "ثلاث أو أربع دورات من ١٠ إلى ١٥ دقيقة. زيارة واحدة وأسرع نتيجة؛ والحساسية تبلغ ذروتها في " +
            "اليوم التالي غالباً.",
        },
        {
          name: "التبييض المنزلي بقوالب مخصّصة",
          text:
            "تُؤخذ طبعات وتُصنع قوالب مناسبة. ويُستخدم بيروكسيد الكارباميد بتركيز ١٠ إلى ١٦٪ لمدة ٣٠–٦٠ " +
            "دقيقة يومياً، أو جل أضعف أثناء النوم، على مدى أسبوعين إلى أربعة. أبطأ وألطف، والنتيجة أكثر " +
            "انتظاماً غالباً لأن الجل يصل إلى القوس كاملاً.",
        },
        {
          name: "الطريقتان معاً",
          text:
            "جلسة في العيادة لرفع الدرجة الأساسية، ثم قوالب منزلية لأسبوع أو أسبوعين لمعادلة عنق السن " +
            "الذي يكون عادةً أغمق جزء. وهذا هو المزيج الذي ينتهي إليه معظم المرضى.",
        },
      ],
    },
    journey: {
      heading: "من الاستشارة إلى الدرجة النهائية",
      steps: [
        {
          name: "التقييم والتنظيف",
          text:
            "فحص التسوّس وصحة اللثة والترميمات القائمة. ويأتي التنظيف والتلميع أولاً: فالتبييض لا يزيل " +
            "الجير، والسطح النظيف هو نقطة البداية العادلة الوحيدة.",
        },
        {
          name: "تسجيل اللون",
          text:
            "صور وقراءة للون في ضوء معياري قبل أي شيء. وبدون ذلك يبقى التغيّر موضع جدل لا قياس.",
        },
        {
          name: "التبييض",
          text:
            "إمّا جلسة واحدة في العيادة مع عزل اللثة، وإمّا قوالب مع إرشادات مكتوبة. ومع القوالب تُظهر " +
            "الأيام الثلاثة الأولى مدى حساسية أسنانك، ويُعدَّل تركيز الجل على هذا الأساس.",
        },
        {
          name: "المراجعة بعد أسبوعين",
          text:
            "يستقرّ اللون خلال أسبوعين تقريباً مع عودة الأسنان إلى ترطيبها الطبيعي، فتُقيَّم الدرجة النهائية " +
            "حينها — لا في يوم العلاج حين تبدو الأسنان أفتح من الحقيقة دائماً.",
        },
        {
          name: "المحافظة على النتيجة",
          text:
            "قوالب إنعاش كل ستة إلى أربعة وعشرين شهراً، و٤٨ ساعة أولى معقولة: لا قهوة ولا نبيذ أحمر ولا " +
            "كركم ولا تدخين في الفترة التي تكون فيها المينا أكثر قابلية للتصبّغ.",
        },
      ],
    },
    risks: {
      heading: "ما قد يسوء",
      intro:
        "التبييض منخفض المخاطر حين يُقيَّم ويُشرف عليه. ومعظم المشاكل تأتي من غياب أحد الأمرين.",
      items: [
        "حساسية أثناء العلاج وليوم أو يومين بعده، وأحياناً أطول؛ وانحسار اللثة القائم يزيدها",
        "تهيّج اللثة أو ابيضاضها حيث يلامس الجل الأنسجة الرخوة — عابر عادةً ويهدأ في أيام",
        "تغيّر ضعيف أو معدوم في التصبّغات الداخلية أو السن معالَج العصب أو التفلور",
        "الترميمات تحتفظ بلونها الأصلي وقد تبدو أغمق بعد أن تفتح الأسنان حولها",
        "التراجع: النتيجة ليست دائمة، وسرعة زوالها تتبع النظام الغذائي والتدخين",
      ],
      note:
        "مزيد من الجل لا يعني بياضاً أكثر. فالجلسات المتكرّرة عالية التركيز دون مراجعة تزيد الحساسية " +
        "وتعطي المينا مظهراً طباشيرياً رقيقاً؛ وإرشادات القالب مكتوبة لسبب.",
    },
    faq: {
      heading: "أسئلة يطرحها المرضى",
      items: [
        {
          q: "هل يضرّ التبييض المينا؟",
          a:
            "بتراكيز تحت إشراف طبي، لا: البيروكسيد يرفع التصبّغ دون إذابة بنية السن بمقدار قابل للقياس، " +
            "ويستعيد المعدن توازنه في الأيام التالية. أما الإفراط — جلسات عالية التركيز متكرّرة بلا تقييم — " +
            "فهو مصدر الضرر والحساسية المستمرة.",
        },
        {
          q: "إلى أي درجة سيبيضّ لون أسناني؟",
          a:
            "لا يمكن الوعد بدرجة مسبقاً. التصبّغ الأصفر المرتبط بالعمر يستجيب أفضل، أما الرمادي وتصبّغ " +
            "التتراسيكلين والتفلور فيستجيب ضعيفاً وأحياناً لا يستجيب. ومعظم المرضى يكسبون درجات قليلة، " +
            "والجواب الصادق نطاق لا رقم.",
        },
        {
          q: "هل هو مؤلم؟",
          a:
            "الجل نفسه غير مؤلم. الحساسية للبارد، وأحياناً وجع حادّ في عنق السن، شائعة ليوم أو يومين — " +
            "وتُخفَّف بتركيز أقل أو مدة أقصر أو جل مزيل للتحسّس قبل الجلسة.",
        },
        {
          q: "كم تدوم النتيجة؟",
          a:
            "من ستة أشهر إلى سنتين عند معظم الناس. والقهوة والشاي والنبيذ الأحمر والكركم والتدخين تعيد " +
            "التصبّغ أسرع؛ وزيارة تنظيف كل ستة أشهر مع قالب إنعاش حين يلزم تحفظ النتيجة ثابتة.",
        },
      ],
    },
    ctaBand: {
      heading: "أرسل صورة في ضوء النهار",
      text:
        "صورة قريبة من نافذة وبلا فلاتر تكفي عادةً لنقول أي نوع من التصبّغ عندك، وهل يُرجَّح نجاح التبييض، " +
        "وهل يجب التخطيط للترميمات حوله.",
      button: "أرسل صورك",
    },
    disclaimer:
      "معلومات عامة وليست نصيحة طبية. لا يمكن الحكم على الملاءمة إلا بعد فحص الأسنان واللثة ومراجعة " +
      "التاريخ المرضي؛ والنتائج تختلف من مريض لآخر.",
  },
};
